import React from 'react';
import { Box, Typography, Card, CardContent, Button, Avatar } from '@mui/material';
import {
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  YouTube as YouTubeIcon,
  Pinterest as PinterestIcon,
  Email as EmailIcon,
  Article as BlogIcon,
  MusicNote as TikTokIcon,
  Upgrade as UpgradeIcon,
} from '@mui/icons-material';
import { XIcon } from '../../components/icons/XIcon';
import { useUserAvailablePlatforms } from '../../hooks/usePlanEntitlements';
import { useAllPlatforms } from '../../hooks/useCommon';
import LoadingSpinner from '../../components/LoadingSpinner';
import { getPlatformDisplayName } from '../../utils/platformUtils';
import { useTranslation } from 'react-i18next';

interface PlatformsCardProps {
  onNavigate: (path: string) => void;
  currentPlan: any;
}

const PlatformsCard: React.FC<PlatformsCardProps> = ({ onNavigate, currentPlan }) => {
  const { t } = useTranslation('dashboard');
  const { data: availablePlatforms, isLoading: loadingAvailable } = useUserAvailablePlatforms();
  const { data: allPlatforms, isLoading: loadingAll } = useAllPlatforms();
  const isFreePlan = !currentPlan || 
    currentPlan.name === 'Free' || 
    currentPlan.name === 'Free Plan' || 
    currentPlan.name === 'FreeTest';
  
  const isLoading = loadingAvailable || loadingAll;
  const availablePlatformIds = new Set((availablePlatforms || []).map((p: any) => p.id));
  const enabledPlatforms = (allPlatforms || []).filter((p: any) => availablePlatformIds.has(p.id));
  const disabledPlatforms = (allPlatforms || []).filter((p: any) => !availablePlatformIds.has(p.id));

  const getPlatformIcon = (platformName: string) => {
    const iconProps = { sx: { fontSize: 24 } };
    
    switch (platformName.toLowerCase()) {
      case 'instagram':
        return <InstagramIcon {...iconProps} />;
      case 'facebook':
        return <FacebookIcon {...iconProps} />;
      case 'twitter':
      case 'x':
        return <XIcon {...iconProps} />;
      case 'linkedin':
        return <LinkedInIcon {...iconProps} />;
      case 'youtube':
        return <YouTubeIcon {...iconProps} />;
      case 'pinterest':
        return <PinterestIcon {...iconProps} />;
      case 'email':
        return <EmailIcon {...iconProps} />;
      case 'blog':
        return <BlogIcon {...iconProps} />;
      case 'tiktok':
        return <TikTokIcon {...iconProps} />;
      default:
        return <BlogIcon {...iconProps} />;
    }
  };

  const getPlatformColor = (platformName: string, colorCode?: string) => {
    // Use backend color_code if provided, otherwise use defaults
    if (colorCode) return colorCode;
    
    switch (platformName.toLowerCase()) {
      case 'instagram':
        return '#E4405F';
      case 'facebook':
        return '#1877F2';
      case 'twitter':
      case 'x':
        return '#000000';
      case 'linkedin':
        return '#0A66C2';
      case 'youtube':
        return '#FF0000';
      case 'pinterest':
        return '#BD081C';
      case 'email':
        return '#6B7280';
      case 'blog':
        return '#10B981';
      case 'tiktok':
        return '#000000';
      default:
        return '#6B7280';
    }
  };

  if (isLoading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <LoadingSpinner message={t('loading')} />
        </CardContent>
      </Card>
    );
  }

  const allPlatformsSorted = [
    ...(allPlatforms || []).filter((p: any) => availablePlatformIds.has(p.id)), // Enabled first
    ...(allPlatforms || []).filter((p: any) => !availablePlatformIds.has(p.id)), // Disabled after
  ];

  return (
    <Card sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      transition: 'all 0.2s ease',
      '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      },
    }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            {t('stats.platformsAvailable')}
          </Typography>
        </Box>

        {allPlatformsSorted.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('stats.noPlatforms')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Platform Icons Grid - Enabled in color, disabled in grayscale */}
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1.5,
            }}>
              {allPlatformsSorted.map((platform: any) => {
                const isEnabled = availablePlatformIds.has(platform.id);
                const platformColor = getPlatformColor(platform.name, platform.color_code);
                
                return (
                  <Avatar
                    key={platform.id || platform.name}
                    sx={{
                      backgroundColor: isEnabled ? platformColor : 'grey.300',
                      width: 40,
                      height: 40,
                      cursor: isEnabled ? 'pointer' : 'default',
                      transition: 'all 0.2s ease',
                      opacity: isEnabled ? 1 : 0.5,
                      filter: isEnabled ? 'none' : 'grayscale(100%)',
                      '&:hover': isEnabled ? {
                        transform: 'scale(1.1)',
                        boxShadow: `0 4px 12px ${platformColor}40`,
                      } : {},
                    }}
                    title={`${getPlatformDisplayName(platform.display_name || platform.name)}${isEnabled ? '' : ` (${t('stats.upgradeToUnlock')})`}`}
                  >
                    {getPlatformIcon(platform.name)}
                  </Avatar>
                );
              })}
            </Box>
            
            {/* Upgrade CTA - Only show if there are disabled platforms */}
            <Box sx={{ mt: 'auto' }}>
              {disabledPlatforms.length > 0 && isFreePlan && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  pt: 1.5,
                }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => onNavigate('/billing')}
                    startIcon={<UpgradeIcon />}
                    sx={{ 
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      color: 'primary.main',
                    }}
                  >
                    {t('stats.unlockMore')}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PlatformsCard;
